import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { onAuth, purgeSession } from '../lib/firebase';
import { apiFetch, type MeResponse } from '../lib/api';
import { Layout } from './Layout';

type Phase = 'checking' | 'allowed' | 'denied';

/**
 * Portão de autenticação do console.
 *
 * Regra inegociável: nada do shell é renderizado antes do Firebase confirmar
 * a sessão E a API confirmar que a conta é um admin ativo. Qualquer estado
 * intermediário mostra apenas a tela de verificação.
 *
 * Uma versão anterior pintava a navegação a partir de um perfil em cache para
 * ganhar tempo de carregamento. Isso expunha a estrutura do painel a quem não
 * estava autenticado — o ganho não compensa e a otimização foi removida.
 */
export function ProtectedLayout() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuth(async (user) => {
      if (cancelled) return;

      // Sem sessão no Firebase: derruba tudo que estiver em memória.
      if (!user) {
        purgeSession();
        setMe(null);
        setAuthError('');
        setPhase('denied');
        return;
      }

      setPhase('checking');

      try {
        const profile = await apiFetch<MeResponse>('/v1/me');
        if (cancelled) return;

        // A API é quem decide quem é admin. Uma resposta sem permissões não
        // habilita nada, então tratamos como recusa.
        if (!profile?.firebase_uid || !Array.isArray(profile.permissions)) {
          throw new Error('Perfil administrativo inválido.');
        }

        setMe(profile);
        setPhase('allowed');
      } catch (e) {
        if (cancelled) return;

        const raw = e instanceof Error ? e.message : '';
        const unreachable = /failed to fetch|networkerror|load failed/i.test(raw);

        // Falha de rede não é autorização. Mesmo offline, negamos o acesso —
        // é preferível a um painel aberto sem validação.
        setAuthError(
          unreachable
            ? 'Não foi possível validar sua sessão com a API do console.'
            : raw || 'Sua conta não tem acesso ao console.',
        );
        purgeSession();
        setMe(null);
        setPhase('denied');
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (phase === 'checking') {
    return (
      <div className="login-page">
        <div className="loading-center">
          <span className="spinner" />
          <span>Verificando acesso…</span>
        </div>
      </div>
    );
  }

  if (phase === 'denied' || !me) {
    return <Navigate to="/login" replace state={{ error: authError }} />;
  }

  return <Layout me={me} />;
}
